import React from "react";
import { Button, Group, Stack, Tooltip } from "@mantine/core";
import SettingsEditButton from "./SettingsEditButton";

export interface SettingsTabShellProps {
  /** Whether this tab is currently in edit mode. */
  editing: boolean;
  /** Hide the Edit affordance entirely (no edit permission). */
  readOnly?: boolean;
  /**
   * Suppress the built-in read-mode Edit button because the page renders the
   * Edit trigger elsewhere (e.g. GiftCardSettings hoists it into the page
   * header). The Cancel/Save footer is unaffected.
   */
  hideEditButton?: boolean;
  /** Save in flight — disables the footer and shows a loader. */
  saving?: boolean;
  /** Enter edit mode. */
  onEdit: () => void;
  /** Leave edit mode without saving. */
  onCancel: () => void;
  /** Persist the current edits. */
  onSave: () => void;
  /** Block saving (e.g. a degraded record) while keeping the button visible. */
  saveDisabled?: boolean;
  /** Tooltip explaining why saving is blocked. */
  saveDisabledReason?: React.ReactNode;
  /** The tab's field rows. */
  children: React.ReactNode;
}

/**
 * Shared chrome for an editable settings tab: a right-aligned Edit button in
 * read mode, and a Cancel / Save footer in edit mode — so every tab toggles the
 * same way. Field rows go in `children`. Used across My Business, Commissions,
 * and other Settings surfaces that follow the redesigned inline read/edit
 * pattern.
 */
export default function SettingsTabShell({
  editing,
  readOnly = false,
  hideEditButton = false,
  saving = false,
  onEdit,
  onCancel,
  onSave,
  saveDisabled = false,
  saveDisabledReason,
  children,
}: SettingsTabShellProps) {
  return (
    <Stack gap={0}>
      {!editing && !readOnly && !hideEditButton && (
        <Group justify="flex-end" mb="sm">
          <SettingsEditButton onClick={onEdit} />
        </Group>
      )}

      {children}

      {editing && (
        <Group justify="flex-end" mt="lg" gap="sm">
          <Button variant="subtle" color="gray" onClick={onCancel}>
            Cancel
          </Button>
          <Tooltip
            label={saveDisabledReason}
            multiline
            w={280}
            disabled={!saveDisabled}
          >
            <Button
              color="var(--brand-teal)"
              // Keep the button visually "disabled" (data-disabled) so its
              // Tooltip still fires on hover, but hard-block the save here —
              // preventDefault on a capture handler does NOT stop this node's
              // own onClick, so the guard must live in the handler itself.
              onClick={() => {
                if (!saveDisabled) onSave();
              }}
              loading={saving}
              data-disabled={saveDisabled || undefined}
              aria-disabled={saveDisabled || undefined}
            >
              Save
            </Button>
          </Tooltip>
        </Group>
      )}
    </Stack>
  );
}
